import { defaultAppearance } from '../character/appearance.js';
import { FRAME_HEIGHT, FRAME_WIDTH } from '../character/compose.js';
import { SPECIES, SPECIES_ORDER } from '../creatures/catalog.js';

/** Stable identity for a brush, used instead of JSON.stringify on every click. */
export function brushKey(brush) {
  if (!brush || typeof brush !== 'object') return '';
  switch (brush.type) {
    case 'tile': return `tile:${brush.tileId}`;
    case 'transition': return `transition:${brush.kind}`;
    case 'elevation': return `elevation:${brush.elevation}`;
    case 'eraseChannel': return `eraseChannel:${brush.channel}`;
    case 'eraseTerrain': return 'eraseTerrain';
    case 'eraseObjects': return 'eraseObjects';
    case 'eraseTransition': return 'eraseTransition';
    case 'eraseCreature': return 'eraseCreature';
    case 'creature': return `creature:${brush.species}`;
    case 'npc': return `npc:${brush.npcId}`;
    case 'collision': return `collision:${brush.state}`;
    default: return String(brush.type);
  }
}

export class Palette {
  constructor(game) {
    this.game = game;
    this.root = null;
    this.brushButtons = [];
    this._navLock = null;
    this._navGen = 0;
    this._navTarget = 0;
  }

  init() {
    this.root = document.getElementById('tile-palette');
    const groups = {
      foundation: document.querySelector('#tile-palette .foundation-options'),
      surface: document.querySelector('#tile-palette .surface-options'),
      surfaces: document.querySelector('#tile-palette .surface-options'),
      outdoorFloors: document.querySelector('#tile-palette .outdoor-floor-options'),
      cliff: document.querySelector('#tile-palette .cliff-options'),
      transition: document.querySelector('#tile-palette .transition-options'),
      exteriors: document.querySelector('#tile-palette .exterior-options'),
      roofs: document.querySelector('#tile-palette .roof-options'),
      exteriorObjects: document.querySelector('#tile-palette .exterior-object-options'),
      interiorFloors: document.querySelector('#tile-palette .interior-floor-options'),
      interiorWalls: document.querySelector('#tile-palette .interior-wall-options'),
      interiorWallObjects: document.querySelector('#tile-palette .interior-wall-object-options'),
      groundObjects: document.querySelector('#tile-palette .object-options'),
      interiorProps: document.querySelector('#tile-palette .object-options'),
      townObjects: document.querySelector('#tile-palette .object-options'),
      townLights: document.querySelector('#tile-palette .object-options'),
      fences: document.querySelector('#tile-palette .fence-options'),
      floraShrubs: document.querySelector('#tile-palette .flora-shrub-options'),
      floraFlowers: document.querySelector('#tile-palette .flora-flower-options'),
      floraGrasses: document.querySelector('#tile-palette .flora-grass-options'),
      floraHedges: document.querySelector('#tile-palette .flora-hedge-options'),
      treeWood: document.querySelector('#tile-palette .tree-wood-options'),
      onGround: document.querySelector('#tile-palette .object-options'),
      wall: document.querySelector('#tile-palette .interior-wall-options'),
      wallObject: document.querySelector('#tile-palette .interior-wall-object-options'),
      exterior: document.querySelector('#tile-palette .exterior-options'),
      interiorFloor: document.querySelector('#tile-palette .interior-floor-options'),
      roof: document.querySelector('#tile-palette .roof-options'),
    };
    const { game } = this;
    for (const tile of game.manifest.tiles.filter((entry) => !['backdrop', 'cliff', 'heightOverlay'].includes(entry.channel))) {
      const brush = tile.channel === 'transition'
        ? { type: 'transition', kind: tile.transitionKind }
        : { type: 'tile', tileId: tile.id };
      const button = this._brushButton(tile.name, tile.previewColor, brush);
      button.dataset.tileId = String(tile.id);
      button.classList.add('tile-option--sprite');
      if (tile.sourceCell) {
        button.style.gridColumn = String(tile.sourceCell.x + 1);
        button.style.gridRow = String(tile.sourceCell.y + 1);
      }
      const firstLayer = Object.keys(tile.components)[0];
      const frame = tile.components[firstLayer][0] ?? Object.values(tile.components[firstLayer])[0];
      const icon = document.createElement('canvas');
      icon.className = 'tile-option__icon';
      icon.width = tile.staticTreeSprite ? 48 : game.manifest.tileSize;
      icon.height = tile.staticTreeSprite ? 48 : game.manifest.tileSize;
      const iconContext = icon.getContext('2d');
      iconContext.imageSmoothingEnabled = false;
      if (tile.staticTreeSprite) {
        const sprite = tile.staticTreeSprite;
        const scale = Math.min(44 / sprite.w, 44 / sprite.h);
        const width = Math.round(sprite.w * scale);
        const height = Math.round(sprite.h * scale);
        iconContext.drawImage(
          game.renderer.atlas,
          sprite.x, sprite.y, sprite.w, sprite.h,
          Math.floor((48 - width) / 2), 48 - height - 2, width, height,
        );
      } else {
        iconContext.drawImage(
          game.renderer.atlas,
          frame.x, frame.y, frame.w, frame.h,
          0, 0, game.manifest.tileSize, game.manifest.tileSize,
        );
      }
      button.append(icon);
      if (tile.staticTreeSprite) {
        const ageBadge = document.createElement('span');
        ageBadge.className = 'tile-option__tree-age';
        ageBadge.textContent = tile.key.includes('-sapling') ? 'S' : tile.key.includes('-young') ? 'Y' : 'O';
        ageBadge.setAttribute('aria-hidden', 'true');
        button.append(ageBadge);
      }
      const group = groups[tile.paletteSection] ?? groups[tile.channel];
      if (!group) {
        console.warn(`No palette container for ${tile.key} (${tile.paletteSection ?? tile.channel})`);
        continue;
      }
      group.append(button);
    }
    groups.foundation.append(this._brushButton(
      'Erase foundation and surface', null,
      { type: 'eraseChannel', channel: 'foundation' }, 'tile-option--erase', '×',
    ));
    groups.surface.append(this._brushButton(
      'Reveal foundation default surface', null,
      { type: 'eraseChannel', channel: 'surface' }, 'tile-option--erase', '×',
    ));
    groups.outdoorFloors.append(this._brushButton(
      'Erase outdoor floor', null,
      { type: 'eraseChannel', channel: 'outdoorFloor' }, 'tile-option--erase', '×',
    ));
    for (const elevation of [1, 2, 3]) groups.cliff.append(this._brushButton(
      `Paint elevation ${elevation}`, '#7b4650', { type: 'elevation', elevation }, '', String(elevation),
    ));
    groups.cliff.append(this._brushButton(
      'Flatten elevation', null, { type: 'elevation', elevation: 0 }, 'tile-option--erase', '0',
    ));
    groups.transition.append(this._brushButton(
      'Erase ramp or stairs', null, { type: 'eraseTransition' }, 'tile-option--erase', '×',
    ));
    groups.onGround.append(this._brushButton(
      'Erase object', null,
      { type: 'eraseChannel', channel: 'onGround' }, 'tile-option--erase', '×',
    ));
    groups.floraHedges.append(this._brushButton(
      'Erase flora', null,
      { type: 'eraseChannel', channel: 'onGround' }, 'tile-option--erase', '×',
    ));
    groups.treeWood.append(this._brushButton(
      'Erase tree', null,
      { type: 'eraseChannel', channel: 'treeWood' }, 'tile-option--erase', '×',
    ));
    for (const [group, channel, label] of [
      [groups.exteriors, 'exterior', 'Erase exterior'],
      [groups.exteriorObjects, 'wallObject', 'Erase wall object'],
      [groups.interiorWallObjects, 'wallObject', 'Erase interior wall object'],
      [groups.interiorFloors, 'interiorFloor', 'Erase interior floor'],
      [groups.interiorWalls, 'wall', 'Erase interior wall'],
      [groups.roofs, 'roof', 'Erase roof'],
    ]) group.append(this._brushButton(label, null, { type: 'eraseChannel', channel }, 'tile-option--erase', '×'));

    const collisionOptions = document.querySelector('#tile-palette .collision-options');
    collisionOptions.append(
      this._brushButton('Inherit collision', '#77818a', { type: 'collision', state: -1 }, '', 'I'),
      this._brushButton('Force passable', '#2ddc7d', { type: 'collision', state: 0 }, '', '○'),
      this._brushButton('Force blocked', '#ef3d3d', { type: 'collision', state: 1 }, '', '×'),
    );
    document.getElementById('show-collision').addEventListener('change', (event) => {
      game.renderer.showCollision = event.currentTarget.checked;
    });
    document.getElementById('show-height').addEventListener('change', (event) => {
      game.renderer.showHeight = event.currentTarget.checked;
    });
    const eraseBrushes = {
      terrain: { type: 'eraseTerrain' },
      objects: { type: 'eraseObjects' },
    };
    for (const button of document.querySelectorAll('[data-erase]')) {
      button._brush = eraseBrushes[button.dataset.erase];
      button._brushKey = brushKey(button._brush);
      this.brushButtons.push(button);
      button.addEventListener('click', () => {
        game.selectedBrush = button._brush;
        this.updateSelected();
        game._updateCursor();
      });
    }
    for (const button of document.querySelectorAll('[data-brush-size]')) {
      button.addEventListener('click', () => {
        const value = button.dataset.brushSize;
        game.brushMode = value === 'area' ? 'area' : 'paint';
        if (game.brushMode === 'paint') game.brushSize = Number(value);
        for (const option of document.querySelectorAll('[data-brush-size]')) {
          option.setAttribute('aria-pressed', String(option === button));
        }
      });
    }
    this._initNav();
    this._initCreaturePalette();
    this._initNpcPalette();
    this.updateSelected();
  }

  toggle() {
    if (!this.root) this.root = document.getElementById('tile-palette');
    this.root.hidden = !this.root.hidden;
  }

  updateSelected() {
    const key = brushKey(this.game.selectedBrush);
    for (const button of this.brushButtons) {
      const pressed = String(button._brushKey === key);
      if (button.getAttribute('aria-pressed') !== pressed) {
        button.setAttribute('aria-pressed', pressed);
      }
    }
  }

  refreshNpcs() {
    const group = document.querySelector('#tile-palette .npc-options');
    if (!group) return;
    this.brushButtons = this.brushButtons.filter((button) => !group.contains(button));
    const npcs = this.game.npcRoster.list();
    const empty = document.querySelector('#tile-palette .npc-empty');
    if (empty) empty.hidden = npcs.length > 0;
    const entries = npcs.map((npc) => this._npcEntry(npc));
    group.replaceChildren(...entries);
    if (npcs.length) {
      group.append(this._brushButton(
        'Erase NPC or animal', null,
        { type: 'eraseCreature' }, 'tile-option--erase', '×',
      ));
    }
    this.updateSelected();
  }

  _initNav() {
    const body = document.querySelector('#tile-palette .palette-body');
    const tabs = [...document.querySelectorAll('[data-palette-tab]')];
    const panels = [...document.querySelectorAll('[data-palette-panel]')];
    if (!body || !tabs.length || !panels.length) return;

    const setActive = (id) => {
      for (const button of tabs) button.setAttribute('aria-pressed', String(button.dataset.paletteTab === id));
    };
    const activateFromScroll = () => {
      if (this._navLock) {
        if (Math.abs(body.scrollTop - this._navTarget) > 8) return;
        this._navLock = null;
      }
      if (body.scrollTop + body.clientHeight >= body.scrollHeight - 2) {
        setActive(panels.at(-1).dataset.palettePanel);
        return;
      }
      const origin = body.getBoundingClientRect().top;
      let current = panels[0];
      for (const panel of panels) {
        if (panel.getBoundingClientRect().top - origin <= 12) current = panel;
      }
      setActive(current.dataset.palettePanel);
    };

    for (const tab of tabs) {
      tab.addEventListener('click', () => {
        const panel = document.getElementById(`palette-${tab.dataset.paletteTab}`);
        if (!panel) return;
        this._navGen += 1;
        const gen = this._navGen;
        this._navLock = tab.dataset.paletteTab;
        setActive(tab.dataset.paletteTab);
        const top = Math.min(
          body.scrollTop + panel.getBoundingClientRect().top - body.getBoundingClientRect().top,
          body.scrollHeight - body.clientHeight,
        );
        this._navTarget = top;
        if (Math.abs(body.scrollTop - top) < 2) {
          this._navLock = null;
          return;
        }
        body.scrollTo({ top, behavior: 'smooth' });
        window.setTimeout(() => {
          if (gen !== this._navGen) return;
          this._navLock = null;
          activateFromScroll();
        }, 1000);
      });
    }
    body.addEventListener('scroll', activateFromScroll, { passive: true });
  }

  _initNpcPalette() {
    document.getElementById('npc-create').addEventListener('click', () => {
      this.game.player.keys.clear();
      this.game.customizer.show(defaultAppearance(), 'npc', { behavior: 'wander', wanderRadius: 'medium' });
    });
    this.refreshNpcs();
  }

  _npcEntry(npc) {
    const { game } = this;
    const placed = game.world.isNpcPlaced(npc.id);
    const entry = document.createElement('div');
    entry.className = 'npc-entry';
    const button = this._brushButton(npc.appearance.name, null, { type: 'npc', npcId: npc.id });
    button.classList.add('tile-option--sprite');
    if (placed) {
      button.classList.add('tile-option--placed');
      button.disabled = true;
      button.title = `${npc.appearance.name} — already in this world`;
    }
    const size = game.manifest.tileSize;
    const icon = document.createElement('canvas');
    icon.className = 'tile-option__icon';
    icon.width = size;
    icon.height = size;
    const context = icon.getContext('2d');
    context.imageSmoothingEnabled = false;
    const scale = size / FRAME_HEIGHT;
    const width = Math.round(FRAME_WIDTH * scale);
    context.drawImage(
      game._npcSprite(npc),
      0, 0, FRAME_WIDTH, FRAME_HEIGHT,
      Math.floor((size - width) / 2), 0, width, size,
    );
    button.append(icon);

    const edit = document.createElement('button');
    edit.type = 'button';
    edit.className = 'npc-entry__edit';
    edit.title = `Edit ${npc.appearance.name}`;
    edit.setAttribute('aria-label', `Edit ${npc.appearance.name}`);
    edit.textContent = '✎';
    edit.addEventListener('click', () => {
      game.player.keys.clear();
      game.customizer.show(npc.appearance, 'npc-edit', {
        id: npc.id,
        behavior: npc.behavior,
        wanderRadius: npc.wanderRadius,
      });
    });
    entry.append(button, edit);
    return entry;
  }

  _initCreaturePalette() {
    const { game } = this;
    const group = document.querySelector('#tile-palette .creature-options');
    if (!group || !game.creatureAtlas || !game.creatureManifest) return;
    for (const id of SPECIES_ORDER) {
      const spec = game.creatureManifest.species[id];
      const catalog = SPECIES[id];
      const button = this._brushButton(catalog.name, null, { type: 'creature', species: id });
      button.classList.add('tile-option--sprite');
      const icon = document.createElement('canvas');
      icon.className = 'tile-option__icon';
      icon.width = game.manifest.tileSize;
      icon.height = game.manifest.tileSize;
      const iconContext = icon.getContext('2d');
      iconContext.imageSmoothingEnabled = false;
      const frame = spec.walk[0];
      const scale = Math.min(icon.width / frame.w, icon.height / frame.h);
      const width = Math.round(frame.w * scale);
      const height = Math.round(frame.h * scale);
      iconContext.drawImage(
        game.creatureAtlas,
        frame.x, frame.y, frame.w, frame.h,
        Math.floor((icon.width - width) / 2),
        icon.height - height,
        width, height,
      );
      button.append(icon);
      group.append(button);
    }
    group.append(this._brushButton(
      'Erase creature', null,
      { type: 'eraseCreature' }, 'tile-option--erase', '×',
    ));
  }

  _brushButton(label, color, brush, extraClass = '', text = '') {
    const button = document.createElement('button');
    button.className = `tile-option ${extraClass}`.trim();
    button.type = 'button';
    button.title = label;
    button.setAttribute('aria-label', label);
    button.setAttribute('aria-pressed', 'false');
    button._brush = brush;
    button._brushKey = brushKey(brush);
    if (color) button.style.setProperty('--tile-color', color);
    button.textContent = text;
    button.addEventListener('click', () => {
      this.game.selectedBrush = brush;
      this.updateSelected();
      this.game._updateCursor();
    });
    this.brushButtons.push(button);
    return button;
  }
}
